import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { money, fmtNumber } from '../../utils/region';
import { useDialDefaults } from '../../utils/dialcode';
import { useReadOnlySeat } from '../../utils/readOnlySeat';

// ── The half that was built and had no screen ───────────────────────────────────────────────────
// An audit of this module kept returning the same sentence: "complete server-side, unreachable from
// any screen". Eight features were in that state — routes written, services tested, and nothing in
// src/ calling them. A feature nobody can reach is indistinguishable from one that was never built,
// except that it also costs maintenance.
//
// Everything here is that recovery. No new server behaviour except where the audit found a defect:
//   · the reporting framework was one CURRENT value per client, so electing TFRS for PAEs this year
//     silently restated which framework last year's filed statements claimed — it is now per period,
//     and a closed period refuses to be restated;
//   · the foreign-currency tax-invoice gate was written, tested, and never called from the document
//     paths, so `currencyOf` accepted any three-letter code — it is wired now.
//
// Operator labels go through t('pa_headless_…'). Statutory Thai — book, statement, register and
// document names, RD form codes, statute references, unit names — is NOT translated: it is what the
// server prints on the document face, so it stays character-for-character identical in every locale
// and an operator reading the Chinese UI still sees which Thai document they are about to issue.
// Server-returned text (d.rule, d.why, d.note, warnings[], x.taxNote, account name_th/name_en, …) is
// DATA and is never wrapped in t().
//
// ⛔ A read-only seat (a client's own books login, the auditor — utils/readOnlySeat.ts) reads every
// panel here and is offered no write: no Add / End holding, no Attach / Declare, no Record, no
// Save, no Revalue, and each select that saves on change (framework, unit, CIT treatment, retention
// years) is read as text. The server refuses every one of those to that login, and each refusal pages
// the admins. Reads stay as staff have them: the PDF, the as-at date, the year, the FX check.

const dmy = (iso: string) => { const p = String(iso || '').split('-'); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : iso; };
const baht = (s: any) => money(Number(s || 0) / 100);
const plain = (n: any) => fmtNumber(n, 2);
const toSatang = (v: string) => Math.round((Number(v) || 0) * 100);
const today = () => new Date().toISOString().slice(0, 10);

const btn: React.CSSProperties = {
  padding: '6px 11px', fontSize: 12, fontWeight: 700, borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-strong)', background: 'var(--bg-surface)', color: 'var(--text-primary)', cursor: 'pointer',
};
const inp: React.CSSProperties = {
  padding: '6px 9px', fontSize: 12.5, borderRadius: 7, border: '1px solid var(--border-strong)',
  background: 'var(--bg-surface)', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box',
};
const row: React.CSSProperties = { display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 9 };
const amber: React.CSSProperties = {
  fontSize: 11.5, color: '#92400E', background: '#FEF6E7', border: '1px solid #F5D9A8',
  padding: '7px 10px', borderRadius: 8, lineHeight: 1.6,
};
const red: React.CSSProperties = {
  fontSize: 11.5, color: '#B42318', background: '#FCEBEB', border: '1px solid #F3B9B9',
  padding: '7px 10px', borderRadius: 8, lineHeight: 1.6,
};
const rule: React.CSSProperties = { fontSize: 11.5, color: 'var(--text-tertiary)', lineHeight: 1.6, marginBottom: 9 };
const th: React.CSSProperties = { textAlign: 'left', fontSize: 10.5, fontWeight: 800, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--text-tertiary)', padding: '5px 8px', whiteSpace: 'nowrap' };
const td: React.CSSProperties = { fontSize: 12, color: 'var(--text-primary)', padding: '5px 8px', borderTop: '1px solid var(--border)', verticalAlign: 'top' };
const num: React.CSSProperties = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };

function Section({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 10, background: 'var(--bg-surface)', overflow: 'hidden' }}>
      <button onClick={() => setOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', gap: 9, width: '100%', padding: '10px 13px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
        <i className={`ti ti-chevron-${open ? 'down' : 'right'}`} style={{ fontSize: 15, color: 'var(--text-tertiary)' }} aria-hidden />
        <span style={{ minWidth: 0, flex: 1 }}>
          <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{title}</span>
          <span style={{ display: 'block', fontSize: 11, color: 'var(--text-tertiary)' }}>{subtitle}</span>
        </span>
      </button>
      {open && <div style={{ padding: '2px 13px 13px' }}>{children}</div>}
    </div>
  );
}
const Err = ({ e }: { e: string }) => e ? <div style={red}>{e}</div> : null;
/** What a select that saves on change says, for a seat that may not change it: the label of the
 *  option it holds, as text. */
const ValueText = ({ children }: { children: React.ReactNode }) =>
  <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-primary)' }}>{children}</span>;

/** #8 บัญชีทำการ — the route existed with no button. */
function WorkingAccount({ clientId, periodId }: { clientId: string; periodId: string }) {
  const { t } = useTranslation();
  const [d, setD] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // ⛔ ONE call, to the endpoint that carries BOTH halves of this panel. It used to call
  // /accounting-extras/working-account and then read d.income, d.expenses, d.netProfit and
  // d.reconciles off it — four names that response has never contained (it returns salesTotal,
  // costOfSales, grossProfit, openingStock, closingStock, unclassified, warnings), so three of the
  // five figures printed 0.00 beside two correct ones and the "does not agree" line could never
  // fire. /ledger-books/statements returns the same working account under `wa` AND the P&L totals
  // the second line needs, so the two lines can no longer come from two different periods either.
  useEffect(() => { api.ledgerStatements(periodId).then(setD).catch((e: any) => setErr(e.message)); }, [clientId, periodId]);
  return (
    <div>
      <div style={rule}>
        {t('pa_headless_wa_rule_a')}<b>{t('pa_headless_wa_rule_b')}</b>{t('pa_headless_wa_rule_c')}
      </div>
      {d?.wa && (
        <div style={{ fontSize: 12.5, color: 'var(--text-primary)', lineHeight: 1.9 }}>
          {t('pa_headless_wa_income')} {baht(d.wa.salesTotal)} · {t('pa_headless_wa_cost')} {baht(d.wa.costOfSales)} · {t('pa_headless_wa_gross')} <b>{baht(d.wa.grossProfit)}</b><br />
          {t('pa_headless_wa_expenses')} {baht(d.pl?.totals?.expense)} · {t('pa_headless_wa_net')} <b>{baht(d.pl?.totals?.profit)}</b>
          {/* The server leaves money on an unplaced account OUT of the gross profit above rather
              than guessing which side it belongs on. A panel that showed the figure without saying
              so would look complete while excluding real revenue. */}
          {!!d.wa.unplaced && <><br /><span style={{ color: '#B45309' }}>{t('pa_headless_wa_unplaced', { n: d.wa.unplaced, amount: baht(d.wa.unplacedAmount) })}</span></>}
        </div>
      )}
      <div style={{ ...row, marginTop: 9 }}>
        <button disabled={busy} style={btn} onClick={async () => {
          setBusy(true); setErr('');
          try { window.open(await api.axDocUrl('working-account.pdf', { clientId, periodId }), '_blank'); }
          catch (e: any) { setErr(e.message); } finally { setBusy(false); }
        }}><i className={busy ? 'ti ti-loader' : 'ti ti-file-type-pdf'} aria-hidden /> {t('pa_headless_wa_doc_name')} · {t('pa_headless_wa_doc')}</button>
      </div>
      <Err e={err} />
    </div>
  );
}

/** #15 the reporting framework — now a fact about the PERIOD. */
function Framework({ clientId, periodId, closed, readOnly }: { clientId: string; periodId: string; closed: boolean; readOnly: boolean }) {
  const { t } = useTranslation();
  const opts: [string, string][] = [['', t('pa_headless_fw_none')], ['npae', t('pa_headless_fw_npae')], ['pae', t('pa_headless_fw_pae')]];
  const [d, setD] = useState<any>(null);
  const [err, setErr] = useState('');
  const load = useCallback(async () => {
    try { const r: any = await api.axNotes(clientId, periodId); setD(r.framework); } catch (e: any) { setErr(e.message); }
  }, [clientId, periodId]);
  useEffect(() => { load(); }, [load]);
  return (
    <div>
      <div style={rule}>
        {t('pa_headless_fw_rule')}
        <br /><b>{t('pa_headless_fw_period')}</b>{t('pa_headless_fw_period_tail')}
      </div>
      <div style={row}>
        {readOnly ? <ValueText>{(opts.find(o => o[0] === (d?.framework || '')) || opts[0])[1]}</ValueText> : (
          <select value={d?.framework || ''} disabled={closed} onChange={async e => {
            setErr('');
            try { await api.axSetFramework(clientId, periodId, e.target.value); await load(); } catch (ex: any) { setErr(ex.message); }
          }} style={{ ...inp, minWidth: 260, opacity: closed ? 0.6 : 1 }}>
            {opts.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
          </select>
        )}
        {d?.dbdForm && <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{t('pa_headless_fw_dbd_form', { form: d.dbdForm })}</span>}
        {closed && <span style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{t('pa_headless_fw_locked')}</span>}
      </div>
      {(d?.warnings || []).map((w: string, i: number) => <div key={i} style={amber}>{w}</div>)}
      <Err e={err} />
    </div>
  );
}

/** #23 / #97 the share register — data model and API existed, no screen. */
function Shareholders({ clientId, readOnly }: { clientId: string; readOnly: boolean }) {
  const { t } = useTranslation();
  const [asAt, setAsAt] = useState(today());
  const [d, setD] = useState<any>(null);
  const { nationality: homeNationality } = useDialDefaults();   // the active company's country's nationality word ('' when none is known)
  const [f, setF] = useState({ nameTh: '', holderKind: 'individual', idNumber: '', nationality: homeNationality, shares: '', paidPerShare: '', heldFrom: '' });
  const [err, setErr] = useState('');
  const load = useCallback(async () => { try { setD(await api.axShareholders(clientId, asAt)); } catch (e: any) { setErr(e.message); } }, [clientId, asAt]);
  useEffect(() => { load(); }, [load]);
  return (
    <div>
      <div style={rule}>
        {t('pa_headless_sh_rule')}
      </div>
      <div style={row}>
        <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{t('pa_headless_sh_as_at')}<br />
          <input type="date" value={asAt} onChange={e => setAsAt(e.target.value)} style={{ ...inp, width: 140 }} /></label>
        {d && <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          {t('pa_headless_sh_holders', { n: d.holders?.length || 0 })} · {t('pa_headless_sh_shares', { n: plain(d.totalShares) })}
          {d.formAvailable === false && <span style={{ color: '#92400E' }}> · {t('pa_headless_sh_form_name')} {t('pa_headless_sh_form_not_held')}</span>}
        </span>}
      </div>
      {!readOnly && <div style={row}>
        <input placeholder={t('pa_headless_sh_name_ph')} value={f.nameTh} onChange={e => setF({ ...f, nameTh: e.target.value })} style={{ ...inp, width: 190 }} />
        <select value={f.holderKind} onChange={e => setF({ ...f, holderKind: e.target.value })} style={inp}>
          <option value="individual">{t('pa_headless_sh_individual')}</option><option value="juristic">{t('pa_headless_sh_juristic')}</option>
        </select>
        <input placeholder={t('pa_headless_sh_idno')} value={f.idNumber} onChange={e => setF({ ...f, idNumber: e.target.value })} style={{ ...inp, width: 140 }} />
        <input placeholder={t('pa_headless_sh_nationality')} value={f.nationality} onChange={e => setF({ ...f, nationality: e.target.value })} style={{ ...inp, width: 90 }} />
        <input placeholder={t('pa_headless_sh_shares_ph')} value={f.shares} onChange={e => setF({ ...f, shares: e.target.value })} style={{ ...inp, width: 110 }} />
        <input placeholder={t('pa_headless_sh_paid_per_share')} value={f.paidPerShare} onChange={e => setF({ ...f, paidPerShare: e.target.value })} style={{ ...inp, width: 120 }} />
        <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{t('pa_headless_sh_held_from')}<br />
          <input type="date" value={f.heldFrom} onChange={e => setF({ ...f, heldFrom: e.target.value })} style={{ ...inp, width: 140 }} /></label>
        <button style={btn} onClick={async () => {
          setErr('');
          try {
            await api.axAddShareholder(clientId, { ...f, shares: Number(f.shares), paidPerShare: Number(f.paidPerShare) });
            setF({ ...f, nameTh: '', idNumber: '', shares: '', paidPerShare: '' }); await load();
          } catch (e: any) { setErr(e.message); }
        }}><i className="ti ti-plus" aria-hidden /> {t('pa_headless_sh_add')}</button>
      </div>}
      {(d?.holders || []).length ? (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={th}>{t('pa_headless_sh_th_name')}</th><th style={th}>{t('pa_headless_sh_th_kind')}</th><th style={th}>{t('pa_headless_sh_idno')}</th><th style={th}>{t('pa_headless_sh_nationality')}</th>
            <th style={{ ...th, textAlign: 'right' }}>{t('pa_headless_sh_th_shares')}</th><th style={{ ...th, textAlign: 'right' }}>%</th><th style={th}>{t('pa_headless_sh_held_from')}</th>{!readOnly && <th style={th} />}</tr></thead>
          <tbody>
            {d.holders.map((h: any) => (
              <tr key={h.id}>
                <td style={td}>{h.name_th || h.nameTh}</td>
                <td style={td}>{(h.holder_kind || h.holderKind) === 'juristic' ? t('pa_headless_sh_juristic') : t('pa_headless_sh_individual')}</td>
                <td style={td}>{h.id_number || h.idNumber || '—'}</td>
                <td style={td}>{h.nationality || '—'}</td>
                <td style={num}>{plain(h.shares)}</td>
                <td style={num}>{d.totalShares ? ((Number(h.shares) / Number(d.totalShares)) * 100).toFixed(2) : '—'}</td>
                <td style={td}>{dmy(h.held_from || h.heldFrom)}</td>
                {!readOnly && <td style={td}>
                  <button style={{ ...btn, padding: '3px 7px', fontSize: 11 }} onClick={async () => {
                    const to = window.prompt(t('pa_headless_sh_end_prompt'), today()); if (!to) return;
                    setErr('');
                    try { await api.axEndHolding(h.id, { heldTo: to }); await load(); } catch (e: any) { setErr(e.message); }
                  }}>{t('pa_headless_sh_end')}</button>
                </td>}
              </tr>
            ))}
          </tbody>
        </table>
      ) : <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('pa_headless_sh_empty')}</div>}
      <Err e={err} />
    </div>
  );
}

/** #21 / #22 the DBD e-Filing package. */
function EfilingPackage({ clientId, periodId, readOnly }: { clientId: string; periodId: string; readOnly: boolean }) {
  const { t } = useTranslation();
  const [d, setD] = useState<any>(null);
  const [unit, setUnit] = useState<any>(null);
  const [err, setErr] = useState('');
  const load = useCallback(async () => {
    try {
      const [a, u]: any[] = await Promise.all([api.axEfilingPackage(clientId, periodId), api.axFsUnit(clientId, periodId).catch(() => null)]);
      setD(a); setUnit(u);
    } catch (e: any) { setErr(e.message); }
  }, [clientId, periodId]);
  useEffect(() => { load(); }, [load]);
  return (
    <div>
      <div style={rule}>
        {t('pa_headless_pkg_rule')}
      </div>
      {unit && (
        <div style={row}>
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>{t('pa_headless_pkg_unit')}</span>
          {readOnly ? (
            // The label the server returns for the unit on file — บาท / พันบาท / ล้านบาท, statutory Thai,
            // untranslated. Never looked up in `options`: those are bare keys, and baht's is '' (falsy).
            <ValueText>{unit.th || unit.en || unit.unit || '—'}</ValueText>
          ) : (
            <select value={unit.unit || ''} onChange={async e => {
              setErr('');
              try { await api.axSetFsUnit(clientId, periodId, e.target.value); await load(); } catch (ex: any) { setErr(ex.message); }
            }} style={inp}>
              {(unit.options || []).map((o: any) => <option key={o.key || o} value={o.key || o}>{o.th || o.label || o}</option>)}
            </select>
          )}
        </div>
      )}
      {(d?.slots || []).map((s: any) => (
        <div key={s.key} style={{ display: 'flex', gap: 9, alignItems: 'center', flexWrap: 'wrap', padding: '7px 0', borderTop: '1px solid var(--border)' }}>
          <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text-primary)', minWidth: 210 }}>{s.th || s.label}</span>
          <span style={{ fontSize: 11.5, color: s.fileId || s.declared ? '#166534' : 'var(--text-tertiary)' }}>
            {s.fileId ? t('pa_headless_pkg_file', { id: s.fileId }) : (s.declared ? t('pa_headless_pkg_declared') : t('pa_headless_pkg_not_attached'))}
          </span>
          {s.producible === false && <span style={{ ...amber, padding: '2px 8px' }}>{s.why || t('pa_headless_pkg_not_producible')}</span>}
          {!readOnly && <button style={{ ...btn, padding: '3px 8px', fontSize: 11 }} onClick={async () => {
            const fid = window.prompt(t('pa_headless_pkg_file_prompt', { slot: s.th || s.label })); if (!fid) return;
            setErr('');
            try { await api.axSetPackageFile(clientId, periodId, { slot: s.key, fileId: fid }); await load(); } catch (e: any) { setErr(e.message); }
          }}>{t('pa_headless_pkg_attach')}</button>}
        </div>
      ))}
      {!readOnly && <div style={{ ...row, marginTop: 9 }}>
        <button style={btn} onClick={async () => {
          setErr('');
          try { await api.axDeclareBw(clientId, periodId); await load(); } catch (e: any) { setErr(e.message); }
        }}>{t('pa_headless_pkg_declare_bw')}</button>
      </div>}
      {(d?.warnings || d?.notes || []).map((w: string, i: number) => <div key={i} style={amber}>{w}</div>)}
      <Err e={err} />
    </div>
  );
}

/** #65 account-level tax treatment — GET wrapper existed, no PATCH, no screen. */
function TaxTreatment({ clientId, periodId, readOnly }: { clientId: string; periodId: string; readOnly: boolean }) {
  const { t } = useTranslation();
  const opts: [string, string][] = [['', t('pa_headless_cit_undecided')], ['yes', t('pa_headless_cit_yes')], ['no', t('pa_headless_cit_no')], ['limited', t('pa_headless_cit_limited')]];
  const [d, setD] = useState<any>(null);
  const [err, setErr] = useState('');
  const load = useCallback(async () => { try { setD(await api.axTaxTreatment(clientId, periodId)); } catch (e: any) { setErr(e.message); } }, [clientId, periodId]);
  useEffect(() => { load(); }, [load]);
  const set = async (id: string, v: string) => {
    setErr('');
    try { await api.axSetAccountTaxTreatment(id, { citDeductible: v }); await load(); } catch (e: any) { setErr(e.message); }
  };
  return (
    <div>
      <div style={rule}>
        {t('pa_headless_cit_rule')}
      </div>
      {(d?.accounts || d?.rows || []).length ? (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={th}>{t('pa_headless_cit_th_account')}</th><th style={{ ...th, textAlign: 'right' }}>{t('pa_headless_cit_th_movement')}</th><th style={th}>{t('pa_headless_cit_th_treatment')}</th><th style={th}>{t('pa_headless_cit_th_note')}</th></tr></thead>
          <tbody>
            {(d.accounts || d.rows).map((a: any) => (
              <tr key={a.id}>
                <td style={td}>{a.code} {a.name_th || a.nameTh}</td>
                <td style={num}>{a.amount != null ? baht(a.amount) : '—'}</td>
                <td style={td}>
                  {readOnly ? (opts.find(o => o[0] === (a.cit_deductible || a.citDeductible || '')) || opts[0])[1] : (
                    <select value={a.cit_deductible || a.citDeductible || ''} onChange={e => set(a.id, e.target.value)} style={{ ...inp, padding: '3px 6px', fontSize: 11.5 }}>
                      {opts.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
                    </select>
                  )}
                </td>
                <td style={{ ...td, fontSize: 11, color: 'var(--text-tertiary)' }}>{a.cit_limit_note || a.citLimitNote || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('pa_headless_cit_empty')}</div>}
      {(d?.warnings || []).map((w: string, i: number) => <div key={i} style={amber}>{w}</div>)}
      <Err e={err} />
    </div>
  );
}

/** #56 the foreign-currency tax-invoice approval — built, tested, and never called until now. */
function FxApproval({ clientId, readOnly }: { clientId: string; readOnly: boolean }) {
  const { t } = useTranslation();
  const [f, setF] = useState({ currency: 'USD', approvalNo: '', approvedOn: '', effectiveFrom: '', effectiveTo: '' });
  const [check, setCheck] = useState<any>(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  return (
    <div>
      <div style={rule}>
        {t('pa_headless_fx_rule_a')}<b>{t('pa_headless_fx_rule_b')}</b>{t('pa_headless_fx_rule_c')}
      </div>
      <div style={row}>
        <input placeholder="USD" value={f.currency} onChange={e => setF({ ...f, currency: e.target.value.toUpperCase() })} style={{ ...inp, width: 80 }} />
        {/* The approval's own fields and Record write; the currency box and Check are a read. */}
        {!readOnly && <>
          <input placeholder={t('pa_headless_fx_approval_no')} value={f.approvalNo} onChange={e => setF({ ...f, approvalNo: e.target.value })} style={{ ...inp, width: 170 }} />
          <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{t('pa_headless_fx_approved_on')}<br />
            <input type="date" value={f.approvedOn} onChange={e => setF({ ...f, approvedOn: e.target.value })} style={{ ...inp, width: 140 }} /></label>
          <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{t('pa_headless_fx_effective_from')}<br />
            <input type="date" value={f.effectiveFrom} onChange={e => setF({ ...f, effectiveFrom: e.target.value })} style={{ ...inp, width: 140 }} /></label>
          <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{t('pa_headless_fx_effective_to')}<br />
            <input type="date" value={f.effectiveTo} onChange={e => setF({ ...f, effectiveTo: e.target.value })} style={{ ...inp, width: 140 }} /></label>
          <button style={btn} onClick={async () => {
            setErr(''); setMsg('');
            try { await api.axAddFxApproval(clientId, f); setMsg(t('pa_headless_fx_recorded', { currency: f.currency })); setF({ ...f, approvalNo: '' }); }
            catch (e: any) { setErr(e.message); }
          }}><i className="ti ti-plus" aria-hidden /> {t('pa_headless_fx_record')}</button>
        </>}
        <button style={btn} onClick={async () => {
          setErr(''); setMsg('');
          try { setCheck(await api.axFxAllowed(clientId, f.currency, today())); } catch (e: any) { setErr(e.message); }
        }}>{t('pa_headless_fx_check', { currency: f.currency })}</button>
      </div>
      {check && <div style={check.allowed ? { ...amber, color: '#166534', background: '#E8F6EE', borderColor: '#BBE5CB' } : red}>{check.why || (check.allowed ? t('pa_headless_fx_allowed') : t('pa_headless_fx_not_allowed'))}</div>}
      {msg && <div style={{ fontSize: 12.5, color: '#166534' }}>{msg}</div>}
      <Err e={err} />
    </div>
  );
}

/** #58 the retention period and the destruction register — machinery existed with no route at all. */
function Retention({ clientId, readOnly }: { clientId: string; readOnly: boolean }) {
  const { t } = useTranslation();
  const opts: [number, string][] = [[5, t('pa_headless_ret_5y')], [6, t('pa_headless_ret_6y')], [7, t('pa_headless_ret_7y')]];
  const [d, setD] = useState<any>(null);
  const [err, setErr] = useState('');
  const load = useCallback(async () => { try { setD(await api.arRetention(clientId)); } catch (e: any) { setErr(e.message); } }, [clientId]);
  useEffect(() => { load(); }, [load]);
  return (
    <div>
      <div style={rule}>{d?.rule}</div>
      <div style={row}>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>{t('pa_headless_ret_label')}</span>
        {/* An installation-wide setting (services/retention): never a client login's to change. */}
        {readOnly ? <ValueText>{(opts.find(o => o[0] === Number(d?.years || 7)) || [0, String(d?.years || '')])[1]}</ValueText> : (
          <select value={d?.years || 7} onChange={async e => {
            setErr('');
            try { await api.arSetRetentionYears(Number(e.target.value)); await load(); } catch (ex: any) { setErr(ex.message); }
          }} style={inp}>
            {opts.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
          </select>
        )}
      </div>
      {(d?.rows || []).length ? (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={th}>{t('pa_headless_ret_th_when')}</th><th style={th}>{t('pa_headless_ret_th_what')}</th><th style={th}>{t('pa_headless_ret_th_mode')}</th><th style={th}>{t('pa_headless_ret_th_reason')}</th><th style={th}>{t('pa_headless_ret_th_by')}</th><th style={th}>SHA-256</th></tr></thead>
          <tbody>
            {d.rows.map((r: any) => (
              <tr key={r.id}>
                <td style={td}>{new Date(Number(r.at) * 1000).toISOString().slice(0, 10)}</td>
                <td style={td}>{r.label || `${r.obj_table} ${String(r.obj_id).slice(0, 8)}`}{r.doc_no ? ` · ${r.doc_no}` : ''}</td>
                <td style={td}>{r.mode}</td>
                <td style={{ ...td, fontSize: 11 }}>{r.reason}</td>
                <td style={td}>{r.by_name || '—'}</td>
                <td style={{ ...td, fontSize: 10.5, fontFamily: 'monospace' }}>{String(r.sha256 || '').slice(0, 12) || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <div style={{ fontSize: 12, color: '#166534' }}>{t('pa_headless_ret_none')}</div>}
      <Err e={err} />
    </div>
  );
}

/** #78 the first general meeting, and every twelve months after. */
function Agm({ clientId }: { clientId: string }) {
  const { t } = useTranslation();
  const [d, setD] = useState<any>(null);
  const [err, setErr] = useState('');
  useEffect(() => { api.arAgm(clientId).then(setD).catch((e: any) => setErr(e.message)); }, [clientId]);
  return (
    <div>
      <div style={rule}>{d?.rule}</div>
      {d?.why ? <div style={amber}>{d.why}</div> : null}
      {d && (
        <div style={{ fontSize: 12.5, color: 'var(--text-primary)', lineHeight: 1.9 }}>
          {d.registeredOn ? <>{t('pa_headless_agm_registered')} {dmy(d.registeredOn)} · {t('pa_headless_agm_first_due')} <b>{dmy(d.firstDueBy)}</b><br /></> : null}
          {d.lastHeldOn ? <>{t('pa_headless_agm_last_held')} {dmy(d.lastHeldOn)}<br /></> : null}
          {d.nextDueBy
            ? <>{t('pa_headless_agm_next_due')} <b style={{ color: d.overdue ? '#B42318' : 'var(--text-primary)' }}>{dmy(d.nextDueBy)}</b>{d.overdue ? ` · ${t('pa_headless_agm_overdue')}` : ''}
                <span style={{ color: 'var(--text-tertiary)' }}> — {d.basis}</span></>
            : null}
        </div>
      )}
      <Err e={err} />
    </div>
  );
}

/** #91 s.54 — who owes the tax when withholding went wrong. */
function WhtLiability({ clientId }: { clientId: string }) {
  const { t } = useTranslation();
  const [year, setYear] = useState(new Date().getFullYear());
  const [d, setD] = useState<any>(null);
  const [err, setErr] = useState('');
  useEffect(() => { api.arWhtLiability(clientId, year).then(setD).catch((e: any) => setErr(e.message)); }, [clientId, year]);
  const atRisk = (d?.payerAlone || 0) + (d?.joint || 0);
  return (
    <div>
      <div style={rule}>{d?.rule}</div>
      <div style={row}>
        <input type="number" value={year} onChange={e => setYear(Number(e.target.value))} style={{ ...inp, width: 90 }} />
        {d && <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          {t('pa_headless_wht_settled', { n: d.settledCount })} · {t('pa_headless_wht_payer_alone')} <b style={{ color: d.payerAlone ? '#B42318' : 'inherit' }}>{plain(d.payerAlone)}</b>
          {' '}· {t('pa_headless_wht_joint')} <b style={{ color: d.joint ? '#92400E' : 'inherit' }}>{plain(d.joint)}</b>
        </span>}
      </div>
      {atRisk > 0 && <div style={red}>{t('pa_headless_wht_at_risk', { amount: plain(atRisk) })} {d.note}</div>}
      {(d?.rows || []).filter((r: any) => r.liable !== 'settled').length ? (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={th}>{t('pa_headless_wht_th_paid')}</th><th style={th}>{t('pa_headless_wht_th_payee')}</th><th style={th}>{t('pa_headless_wht_th_form')}</th>
            <th style={{ ...th, textAlign: 'right' }}>{t('pa_headless_wht_th_gross')}</th><th style={{ ...th, textAlign: 'right' }}>{t('pa_headless_wht_th_withheld')}</th>
            <th style={th}>{t('pa_headless_wht_th_who')}</th><th style={th}>{t('pa_headless_wht_th_why')}</th></tr></thead>
          <tbody>
            {d.rows.filter((r: any) => r.liable !== 'settled').map((r: any) => (
              <tr key={r.id}>
                <td style={td}>{dmy(r.payDate)}</td>
                <td style={td}>{r.payeeName}</td>
                <td style={td}>{String(r.form).toUpperCase().replace('PND', 'PND.')}</td>
                <td style={num}>{plain(r.gross)}</td>
                <td style={num}>{r.wasWithheld ? plain(r.withheld) : '—'}</td>
                <td style={{ ...td, color: r.liable === 'payer_alone' ? '#B42318' : '#92400E' }}>{r.liableLabel}</td>
                <td style={{ ...td, fontSize: 11, color: 'var(--text-tertiary)' }}>{r.why}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <div style={{ fontSize: 12, color: '#166534' }}>{t('pa_headless_wht_all_clear')}</div>}
      <Err e={err} />
    </div>
  );
}

/** #94 the employer's own social-security registration. */
function EmployerSso({ clientId, readOnly }: { clientId: string; readOnly: boolean }) {
  const { t } = useTranslation();
  const [d, setD] = useState<any>(null);
  const [f, setF] = useState<any>({ accountNo: '', branchCode: '', registeredOn: '', form101FiledOn: '', office: '', firstEmployeeOn: '' });
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const load = useCallback(async () => {
    try {
      const r: any = await api.arEmployerSso(clientId); setD(r);
      if (r.row) setF({ accountNo: r.row.accountNo, branchCode: r.row.branchCode, registeredOn: r.row.registeredOn,
        form101FiledOn: r.row.form101FiledOn, office: r.row.office, firstEmployeeOn: r.row.firstEmployeeOn });
    } catch (e: any) { setErr(e.message); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);
  return (
    <div>
      <div style={rule}>
        {t('pa_headless_sso_rule_a')}<b>{t('pa_headless_sso_rule_b')}</b>{t('pa_headless_sso_rule_c')}
      </div>
      {d?.gap && <div style={red}>{d.gap}</div>}
      {/* A form saved by its button: a seat that may not save it reads what is on file instead. */}
      {readOnly ? (!!d?.row && (
        <div style={row}>
          {([['pa_headless_sso_account_no', f.accountNo], ['pa_headless_sso_branch', f.branchCode], ['pa_headless_sso_registered_on', dmy(f.registeredOn)],
            ['pa_headless_sso_form_101', dmy(f.form101FiledOn)], ['pa_headless_sso_office', f.office]] as [string, string][]).map(([k, v]) => (
            <span key={k} style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{t(k)}<br />
              <span style={{ fontSize: 12.5, color: 'var(--text-primary)' }}>{v || '—'}</span></span>
          ))}
        </div>
      )) : (
        <div style={row}>
          <input placeholder={t('pa_headless_sso_account_no')} value={f.accountNo} onChange={e => setF({ ...f, accountNo: e.target.value })} style={{ ...inp, width: 180 }} />
          <input placeholder={t('pa_headless_sso_branch')} value={f.branchCode} onChange={e => setF({ ...f, branchCode: e.target.value })} style={{ ...inp, width: 80 }} />
          <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{t('pa_headless_sso_registered_on')}<br />
            <input type="date" value={f.registeredOn} onChange={e => setF({ ...f, registeredOn: e.target.value })} style={{ ...inp, width: 140 }} /></label>
          <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{t('pa_headless_sso_form_101')}<br />
            <input type="date" value={f.form101FiledOn} onChange={e => setF({ ...f, form101FiledOn: e.target.value })} style={{ ...inp, width: 140 }} /></label>
          <input placeholder={t('pa_headless_sso_office')} value={f.office} onChange={e => setF({ ...f, office: e.target.value })} style={{ ...inp, width: 170 }} />
          <button style={btn} onClick={async () => {
            setErr(''); setMsg('');
            try { await api.arSaveEmployerSso(clientId, f); setMsg(t('pa_headless_sso_saved')); await load(); } catch (e: any) { setErr(e.message); }
          }}><i className="ti ti-device-floppy" aria-hidden /> {t('pa_headless_sso_save')}</button>
          {msg && <span style={{ fontSize: 12, color: '#166534' }}>{msg}</span>}
        </div>
      )}
      {d && <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
        {t('pa_headless_sso_employees', { n: d.employees })}{d.employeesWithoutSsoId ? ` · ${t('pa_headless_sso_no_number', { n: d.employeesWithoutSsoId })}` : ''}
      </div>}
      <Err e={err} />
    </div>
  );
}

/** #92 an upward revaluation, and the two bases it creates. */
function AssetBases({ clientId, periodId, readOnly }: { clientId: string; periodId: string; readOnly: boolean }) {
  const { t } = useTranslation();
  const [d, setD] = useState<any>(null);
  const [err, setErr] = useState('');
  const load = useCallback(async () => { try { setD(await api.arAssetBases(clientId)); } catch (e: any) { setErr(e.message); } }, [clientId]);
  useEffect(() => { load(); }, [load]);
  return (
    <div>
      <div style={rule}>
        {t('pa_headless_rev_rule_a')}<b>{t('pa_headless_rev_rule_b')}</b>{t('pa_headless_rev_rule_c')}
      </div>
      {(d?.rows || []).length ? (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={th}>{t('pa_headless_rev_th_asset')}</th><th style={{ ...th, textAlign: 'right' }}>{t('pa_headless_rev_th_cost')}</th>
            <th style={{ ...th, textAlign: 'right' }}>{t('pa_headless_rev_th_book')}</th><th style={{ ...th, textAlign: 'right' }}>{t('pa_headless_rev_th_tax')}</th>
            <th style={{ ...th, textAlign: 'right' }}>{t('pa_headless_rev_th_reval')}</th>{!readOnly && <th style={th} />}</tr></thead>
          <tbody>
            {d.rows.map((r: any) => (
              <tr key={r.assetId}>
                <td style={td}>{r.name}</td>
                <td style={num}>{baht(r.costSatang)}</td>
                <td style={num}>{baht(r.bookBasisSatang)}</td>
                <td style={num}>{baht(r.taxBasisSatang)}</td>
                <td style={{ ...num, color: r.divergent ? '#92400E' : 'var(--text-tertiary)' }}>{r.revaluationGainSatang ? baht(r.revaluationGainSatang) : '—'}</td>
                {!readOnly && <td style={td}>
                  <button style={{ ...btn, padding: '3px 7px', fontSize: 11 }} onClick={async () => {
                    const after = window.prompt(t('pa_headless_rev_amount_prompt', { name: r.name }), String(r.bookBasisSatang / 100)); if (!after) return;
                    const on = window.prompt(t('pa_headless_rev_date_prompt'), today()); if (!on) return;
                    const basis = window.prompt(t('pa_headless_rev_basis_prompt')); if (!basis) return;
                    setErr('');
                    try {
                      const x: any = await api.arRevalue(clientId, { assetId: r.assetId, revaluedOn: on, bookAfter: toSatang(after), basis, periodId });
                      window.alert(x.taxNote); await load();
                    } catch (e: any) { setErr(e.message); }
                  }}>{t('pa_headless_rev_revalue')}</button>
                </td>}
              </tr>
            ))}
          </tbody>
        </table>
      ) : <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('pa_headless_rev_empty')}</div>}
      {d?.note && <div style={amber}>{d.note}</div>}
      <Err e={err} />
    </div>
  );
}

/**
 * #71 บัญชีสินค้า — the items, what is on hand, and the period's struck closing balance.
 *
 * The survey of 2026-10-20 found the shape this file was built to fix, one layer down:
 * createItem() was the ONLY writer of stock_items and strikeBalances() the only writer of
 * stock_balances, and neither had a screen. Two screens that DO exist read them — the stock
 * valuation register (Registers.tsx, s.65 bis (6)-(7)) and บัญชีทำการ above — so for every client
 * both could only ever render their empty branch: "This client has no stock account." and
 * "ไม่มีบัญชีสินค้า … That is correct for a services business."
 *
 * Every refusal here is the server's own and none is re-implemented on this side: a missing
 * หน่วยนับ, a missing Thai name, a duplicate code, an issue larger than what is on hand. The
 * value column is satang, as it is everywhere the movements are read.
 */
function StockAccount({ clientId, periodId }: { clientId: string; periodId: string }) {
  const { t } = useTranslation();
  const [d, setD] = useState<any>(null);
  const [f, setF] = useState({ code: '', nameTh: '', nameEn: '', itemType: '', unit: '', kind: 'goods' });
  const [struck, setStruck] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const load = useCallback(async () => {
    try { setD(await api.axStockItems(clientId)); } catch (e: any) { setErr(e.message); }
  }, [clientId]);
  useEffect(() => { load(); }, [load]);
  const items: any[] = d?.items || [];
  return (
    <div>
      <div style={rule}>{t('pa_headless_stk_rule')}</div>
      <div style={row}>
        <input placeholder={t('pa_headless_stk_code_ph')} value={f.code} onChange={e => setF({ ...f, code: e.target.value })} style={{ ...inp, width: 100 }} />
        <input placeholder={t('pa_headless_stk_name_ph')} value={f.nameTh} onChange={e => setF({ ...f, nameTh: e.target.value })} style={{ ...inp, width: 170 }} />
        <input placeholder={t('pa_headless_stk_name_en_ph')} value={f.nameEn} onChange={e => setF({ ...f, nameEn: e.target.value })} style={{ ...inp, width: 140 }} />
        <input placeholder={t('pa_headless_stk_type_ph')} value={f.itemType} onChange={e => setF({ ...f, itemType: e.target.value })} style={{ ...inp, width: 120 }} />
        <input placeholder={t('pa_headless_stk_unit_ph')} value={f.unit} onChange={e => setF({ ...f, unit: e.target.value })} style={{ ...inp, width: 90 }} />
        <select value={f.kind} onChange={e => setF({ ...f, kind: e.target.value })} style={inp}>
          <option value="goods">{t('pa_headless_stk_goods')}</option>
          <option value="raw_material">{t('pa_headless_stk_raw')}</option>
        </select>
        <button style={btn} onClick={async () => {
          setErr('');
          try { await api.axAddStockItem(clientId, f); setF({ ...f, code: '', nameTh: '', nameEn: '' }); await load(); }
          catch (e: any) { setErr(e.message); }
        }}><i className="ti ti-plus" aria-hidden /> {t('pa_headless_stk_add')}</button>
      </div>
      {items.length ? (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={th}>{t('pa_headless_stk_th_code')}</th><th style={th}>{t('pa_headless_stk_th_name')}</th>
            <th style={th}>{t('pa_headless_stk_th_unit')}</th><th style={th}>{t('pa_headless_stk_th_kind')}</th>
            <th style={{ ...th, textAlign: 'right' }}>{t('pa_headless_stk_th_onhand')}</th>
            <th style={{ ...th, textAlign: 'right' }}>{t('pa_headless_stk_th_value')}</th></tr></thead>
          <tbody>
            {items.map((i: any) => (
              <tr key={i.id}>
                <td style={td}>{i.code}</td>
                <td style={td}>{i.name_th || i.nameTh}{(i.name_en || i.nameEn) ? <span style={{ color: 'var(--text-tertiary)' }}> · {i.name_en || i.nameEn}</span> : null}</td>
                <td style={td}>{i.unit}</td>
                <td style={td}>{String(i.kind) === 'raw_material' ? t('pa_headless_stk_raw') : t('pa_headless_stk_goods')}</td>
                <td style={num}>{plain(i.onHand?.quantity)}</td>
                <td style={num}>{baht(i.onHand?.value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('pa_headless_stk_empty')}</div>}
      <div style={{ ...row, marginTop: 9 }}>
        <button disabled={busy || !items.length} style={{ ...btn, opacity: (busy || !items.length) ? 0.55 : 1 }} onClick={async () => {
          setBusy(true); setErr(''); setStruck(null);
          try { setStruck(await api.axStrikeStock(clientId, periodId)); await load(); }
          catch (e: any) { setErr(e.message); } finally { setBusy(false); }
        }}><i className={busy ? 'ti ti-loader' : 'ti ti-stack-2'} aria-hidden /> {t('pa_headless_stk_strike')}</button>
        {struck && <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          {t('pa_headless_stk_struck', { n: struck.items, on: dmy(struck.struckOn), due: dmy(struck.dueBy) })}
        </span>}
      </div>
      {struck?.late && <div style={amber}>{t('pa_headless_stk_late', { due: dmy(struck.dueBy) })}</div>}
      <Err e={err} />
    </div>
  );
}

/**
 * #38 / #40 the auditor elected for the period, and what is still missing.
 *
 * electAuditor() was the only writer of auditor_elections and had no screen, which made the
 * problem list of GET /pnd50-attachments — whose `certifier` reads this very table — impossible
 * to clear from anywhere in the product rather than merely unfilled.
 *
 * `problems[]` and the election warnings are SERVER text and are printed as data. The remuneration
 * column is `REAL` and nothing in the server divides it by 100, so it is sent and shown in whole
 * currency units, not satang — the one assumption in this panel, and it is flagged to the operator
 * rather than hidden in a formatter.
 */
function Auditor({ clientId, periodId }: { clientId: string; periodId: string }) {
  const { t } = useTranslation();
  const [d, setD] = useState<any>(null);
  const [f, setF] = useState({ electedOn: '', auditorName: '', auditorLicense: '', auditorKind: '', licenseExpires: '', remuneration: '', independenceDeclared: false, independenceNote: '' });
  const [warn, setWarn] = useState<string[]>([]);
  const [err, setErr] = useState('');
  const load = useCallback(async () => {
    try { setD(await api.axAuditorStatus(clientId, periodId)); } catch (e: any) { setErr(e.message); }
  }, [clientId, periodId]);
  useEffect(() => { load(); }, [load]);
  const cur = d?.current;
  return (
    <div>
      <div style={rule}>{t('pa_headless_aud_rule')}</div>
      {cur ? (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 9 }}>
          <thead><tr><th style={th}>{t('pa_headless_aud_th_elected')}</th><th style={th}>{t('pa_headless_aud_th_name')}</th>
            <th style={th}>{t('pa_headless_aud_th_kind')}</th><th style={th}>{t('pa_headless_aud_th_license')}</th>
            <th style={th}>{t('pa_headless_aud_th_expires')}</th><th style={{ ...th, textAlign: 'right' }}>{t('pa_headless_aud_th_remun')}</th>
            <th style={th}>{t('pa_headless_aud_th_indep')}</th></tr></thead>
          <tbody><tr>
            <td style={td}>{dmy(cur.elected_on)}</td>
            <td style={td}>{cur.auditor_name}</td>
            <td style={td}>{String(cur.auditor_kind) === 'cpa' ? t('pa_headless_aud_kind_cpa') : String(cur.auditor_kind) === 'tax_auditor' ? t('pa_headless_aud_kind_ta') : '—'}</td>
            <td style={td}>{cur.auditor_license || '—'}</td>
            <td style={td}>{cur.license_expires ? dmy(cur.license_expires) : '—'}</td>
            <td style={num}>{Number(cur.remuneration) ? plain(cur.remuneration) : '—'}</td>
            <td style={td}>{Number(cur.independence_declared) ? t('pa_headless_aud_yes') : t('pa_headless_aud_no')}</td>
          </tr></tbody>
        </table>
      ) : <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 9 }}>{t('pa_headless_aud_none')}</div>}
      {!cur && (
        <>
          <div style={row}>
            <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{t('pa_headless_aud_elected_on')}<br />
              <input type="date" value={f.electedOn} onChange={e => setF({ ...f, electedOn: e.target.value })} style={{ ...inp, width: 140 }} /></label>
            <input placeholder={t('pa_headless_aud_name_ph')} value={f.auditorName} onChange={e => setF({ ...f, auditorName: e.target.value })} style={{ ...inp, width: 180 }} />
            <select value={f.auditorKind} onChange={e => setF({ ...f, auditorKind: e.target.value })} style={inp}>
              <option value="">{t('pa_headless_aud_kind_none')}</option>
              <option value="cpa">{t('pa_headless_aud_kind_cpa')}</option>
              <option value="tax_auditor">{t('pa_headless_aud_kind_ta')}</option>
            </select>
            <input placeholder={t('pa_headless_aud_license_ph')} value={f.auditorLicense} onChange={e => setF({ ...f, auditorLicense: e.target.value })} style={{ ...inp, width: 120 }} />
            <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{t('pa_headless_aud_expires')}<br />
              <input type="date" value={f.licenseExpires} onChange={e => setF({ ...f, licenseExpires: e.target.value })} style={{ ...inp, width: 140 }} /></label>
            <input placeholder={t('pa_headless_aud_remun_ph')} value={f.remuneration} onChange={e => setF({ ...f, remuneration: e.target.value })} style={{ ...inp, width: 120 }} />
          </div>
          <div style={row}>
            <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={f.independenceDeclared} onChange={e => setF({ ...f, independenceDeclared: e.target.checked })} />
              {t('pa_headless_aud_independence')}
            </label>
            <input placeholder={t('pa_headless_aud_independence_note_ph')} value={f.independenceNote} onChange={e => setF({ ...f, independenceNote: e.target.value })} style={{ ...inp, width: 240 }} />
            <button style={btn} onClick={async () => {
              setErr(''); setWarn([]);
              try {
                const r: any = await api.axElectAuditor(clientId, { ...f, periodId, remuneration: Number(f.remuneration) || 0 });
                setWarn(r.warnings || []); await load();
              } catch (e: any) { setErr(e.message); }
            }}><i className="ti ti-gavel" aria-hidden /> {t('pa_headless_aud_elect')}</button>
          </div>
          <div style={{ ...rule, marginTop: -3 }}>{t('pa_headless_aud_remun_note')}</div>
        </>
      )}
      {warn.map((w, i) => <div key={i} style={amber}>{w}</div>)}
      {(d?.problems || []).length
        ? (d.problems as string[]).map((pb, i) => <div key={i} style={amber}>{pb}</div>)
        : d ? <div style={{ fontSize: 12, color: '#166534' }}>{t('pa_headless_aud_clear')}</div> : null}
      <Err e={err} />
    </div>
  );
}

/**
 * #51 ภ.พ.30 coverage — which months of a year carry no VAT line at all.
 *
 * pp30Coverage() had an api.ts helper (axPp30) and no component calling it, which is the quieter
 * half of "no screen": a single-hop grep for the route path finds the helper and reports the route
 * as reachable. Six real clients have between 2 and 11 empty months in 2026 today.
 *
 * `total` on tax_lines is in WHOLE currency units — vatReportHtml multiplies it by 100 before
 * printing — so these figures use money() directly and not the satang helper above.
 * `consolidated.why` and `note` are server text.
 */
function Pp30Coverage({ clientId }: { clientId: string }) {
  const { t } = useTranslation();
  const [year, setYear] = useState(new Date().getFullYear());
  const [d, setD] = useState<any>(null);
  const [err, setErr] = useState('');
  useEffect(() => { setErr(''); api.axPp30(clientId, year).then(setD).catch((e: any) => setErr(e.message)); }, [clientId, year]);
  const whole = (v: any) => money(Number(v || 0));
  const empties: any[] = d?.emptyMonths || [];
  return (
    <div>
      <div style={rule}>{t('pa_headless_pp30_rule')}</div>
      <div style={row}>
        <input type="number" value={year} onChange={e => setYear(Number(e.target.value))} style={{ ...inp, width: 90 }} />
        {d && <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
          {empties.length
            ? <b style={{ color: '#92400E' }}>{t('pa_headless_pp30_empty_months', { n: empties.length })}</b>
            : <span style={{ color: '#166534' }}>{t('pa_headless_pp30_all_covered')}</span>}
          {d.establishments?.length ? ` · ${t('pa_headless_pp30_estabs', { n: d.establishments.length })}` : ''}
        </span>}
      </div>
      {d?.consolidated?.why && <div style={rule}>{d.consolidated.why}</div>}
      {d?.perEstablishment && <div style={amber}>{t('pa_headless_pp30_per_estab')}</div>}
      {d && (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={th}>{t('pa_headless_pp30_th_month')}</th>
            <th style={{ ...th, textAlign: 'right' }}>{t('pa_headless_pp30_th_sales')}</th>
            <th style={{ ...th, textAlign: 'right' }}>{t('pa_headless_pp30_th_purchases')}</th>
            <th style={{ ...th, textAlign: 'right' }}>{t('pa_headless_pp30_th_lines')}</th>
            <th style={th}>{t('pa_headless_pp30_th_state')}</th></tr></thead>
          <tbody>
            {(d.months || []).map((m: any) => (
              <tr key={m.month}>
                <td style={td}>{String(m.month).padStart(2, '0')}/{m.year}</td>
                <td style={num}>{whole(m.sales)}</td>
                <td style={num}>{whole(m.purchases)}</td>
                <td style={num}>{plain(m.lines)}</td>
                <td style={{ ...td, color: m.empty ? '#92400E' : '#166534' }}>
                  {m.empty ? t('pa_headless_pp30_state_empty') : t('pa_headless_pp30_state_ok')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {d?.note && <div style={amber}>{d.note}</div>}
      <Err e={err} />
    </div>
  );
}

/**
 * The เลขที่แฟ้ม column the purchase VAT report already prints, and the only thing that fills it.
 * The service numbers ONLY lines that have none, continuing from the highest number already in
 * that tax month, so a report reprinted after a late invoice arrives does not renumber anything
 * already sitting in a physical folder. `from` is meaningless when nothing was assigned (the
 * service still computes one), so it is shown only when there is something to show.
 *
 * ⛔ A FILED MONTH STILL ACCEPTS NUMBERS, and that is the owner's decision (2026-10-20), not an
 * oversight — do not propose a filed-month gate here again. No route in routes/accountingExtras.ts
 * is behind filedLock.ts, and this one was looked at specifically: numbering can only ever FILL
 * BLANKS and can never change a number already given, so it is not the "editing a filed month"
 * case that lock exists for. A late purchase invoice landing in a month already filed still needs
 * its เลขที่แฟ้ม, and the report that prints it is the same report either way.
 */
function ArchiveNumbers({ clientId }: { clientId: string }) {
  const { t } = useTranslation();
  const now = new Date();
  const [f, setF] = useState({ year: now.getUTCFullYear(), month: now.getUTCMonth() + 1 });
  const [d, setD] = useState<{ assigned: number; from: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  return (
    <div>
      <div style={rule}>{t('pa_headless_arc_rule')}</div>
      <div style={row}>
        <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{t('pa_headless_arc_year')}<br />
          <input type="number" value={f.year} onChange={e => setF({ ...f, year: Number(e.target.value) || f.year })} style={{ ...inp, width: 90 }} /></label>
        <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{t('pa_headless_arc_month')}<br />
          <select value={f.month} onChange={e => setF({ ...f, month: Number(e.target.value) })} style={{ ...inp, width: 90 }}>
            {Array.from({ length: 12 }, (_, i) => i + 1).map(m => <option key={m} value={m}>{String(m).padStart(2, '0')}</option>)}
          </select></label>
        <button style={btn} disabled={busy} onClick={async () => {
          setBusy(true); setErr(''); setD(null);
          try { setD(await api.axAssignArchiveNumbers(clientId, f.year, f.month) as any); }
          catch (e: any) { setErr(e.message); } finally { setBusy(false); }
        }}><i className={busy ? 'ti ti-loader' : 'ti ti-list-numbers'} aria-hidden /> {t('pa_headless_arc_assign')}</button>
      </div>
      {d && (d.assigned
        ? <div style={{ ...amber, color: '#166534', background: '#E8F6EE', borderColor: '#BBE5CB' }}>
            {t('pa_headless_arc_done', { n: d.assigned, from: d.from })}
          </div>
        : <div style={amber}>{t('pa_headless_arc_none')}</div>)}
      <Err e={err} />
    </div>
  );
}

/**
 * Tax withheld FROM the client (the register, and what may be credited). Before this there was no
 * register at all and the annual return's credit was typed from a drawer of paper certificates.
 * The screen keeps the server's own split: a credit claimed WITHOUT the certificate in hand is
 * shown apart, because that is the one an assessment officer removes.
 * Amounts are entered in baht and sent in satang, which is how the register stores them.
 */
function WhtSuffered({ clientId, periodId }: { clientId: string; periodId: string }) {
  const { t } = useTranslation();
  const blank = { payDate: '', payerName: '', payerTaxId: '', source: 'customer', incomeType: '',
                  gross: '', rate: '', withheld: '', certificateNo: '', certificateReceived: false, note: '' };
  const [f, setF] = useState(blank);
  const [d, setD] = useState<any>(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const load = useCallback(async () => {
    if (!periodId) { setD(null); return; }
    try { setD(await api.axWhtCredit(clientId, periodId)); setErr(''); } catch (e: any) { setD(null); setErr(e.message); }
  }, [clientId, periodId]);
  useEffect(() => { load(); }, [load]);
  const SOURCES = ['customer', 'government', 'land_office', 'other'] as const;
  return (
    <div>
      <div style={rule}>{t('pa_headless_whs_rule')}</div>
      <div style={row}>
        <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{t('pa_headless_whs_pay_date')}<br />
          <input type="date" value={f.payDate} onChange={e => setF({ ...f, payDate: e.target.value })} style={{ ...inp, width: 140 }} /></label>
        <input placeholder={t('pa_headless_whs_payer')} value={f.payerName} onChange={e => setF({ ...f, payerName: e.target.value })} style={{ ...inp, width: 190 }} />
        <input placeholder={t('pa_headless_whs_payer_tax_id')} value={f.payerTaxId} onChange={e => setF({ ...f, payerTaxId: e.target.value })} style={{ ...inp, width: 150 }} />
        <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{t('pa_headless_whs_source')}<br />
          <select value={f.source} onChange={e => setF({ ...f, source: e.target.value })} style={{ ...inp, width: 150 }}>
            {SOURCES.map(s => <option key={s} value={s}>{t(`pa_headless_whs_src_${s}`)}</option>)}
          </select></label>
        <input placeholder={t('pa_headless_whs_income_type')} value={f.incomeType} onChange={e => setF({ ...f, incomeType: e.target.value })} style={{ ...inp, width: 150 }} />
      </div>
      <div style={row}>
        <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{t('pa_headless_whs_gross')}<br />
          <input inputMode="decimal" value={f.gross} onChange={e => setF({ ...f, gross: e.target.value })} style={{ ...inp, width: 130, textAlign: 'right' }} /></label>
        <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{t('pa_headless_whs_rate')}<br />
          <input inputMode="decimal" value={f.rate} onChange={e => setF({ ...f, rate: e.target.value })} style={{ ...inp, width: 80, textAlign: 'right' }} /></label>
        <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{t('pa_headless_whs_withheld')}<br />
          <input inputMode="decimal" value={f.withheld} onChange={e => setF({ ...f, withheld: e.target.value })} style={{ ...inp, width: 130, textAlign: 'right' }} /></label>
        <input placeholder={t('pa_headless_whs_cert_no')} value={f.certificateNo} onChange={e => setF({ ...f, certificateNo: e.target.value })} style={{ ...inp, width: 150 }} />
        <label style={{ fontSize: 11.5, color: 'var(--text-secondary)', display: 'flex', alignItems: 'center', gap: 5, paddingBottom: 6 }}>
          <input type="checkbox" checked={f.certificateReceived} onChange={e => setF({ ...f, certificateReceived: e.target.checked })} />
          {t('pa_headless_whs_cert_received')}
        </label>
        <button style={btn} onClick={async () => {
          setErr(''); setMsg('');
          try {
            await api.axAddWhtSuffered(clientId, { periodId, payDate: f.payDate, payerName: f.payerName,
              payerTaxId: f.payerTaxId, source: f.source, incomeType: f.incomeType,
              gross: toSatang(f.gross), rate: Number(f.rate) || 0, withheld: toSatang(f.withheld),
              certificateNo: f.certificateNo, certificateReceived: f.certificateReceived, note: f.note });
            setMsg(t('pa_headless_whs_recorded')); setF(blank); await load();
          } catch (e: any) { setErr(e.message); }
        }}><i className="ti ti-plus" aria-hidden /> {t('pa_headless_whs_record')}</button>
      </div>
      {d && (
        <>
          <div style={row}>
            <span style={{ fontSize: 12 }}>{t('pa_headless_whs_total')} <b>{baht(d.total)}</b></span>
            <span style={{ fontSize: 12, color: '#166534' }}>{t('pa_headless_whs_with_cert')} <b>{baht(d.withCertificate)}</b></span>
            <span style={{ fontSize: 12, color: d.withoutCertificate ? '#92400E' : 'var(--text-tertiary)' }}>
              {t('pa_headless_whs_without_cert')} <b>{baht(d.withoutCertificate)}</b>
            </span>
          </div>
          {!!d.bySource?.length && (
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 9 }}>
              <thead><tr><th style={th}>{t('pa_headless_whs_source')}</th><th style={{ ...th, textAlign: 'right' }}>{t('pa_headless_whs_count')}</th><th style={{ ...th, textAlign: 'right' }}>{t('pa_headless_whs_withheld')}</th></tr></thead>
              <tbody>{d.bySource.map((s: any) => (
                <tr key={s.source}>
                  <td style={td}>{t(`pa_headless_whs_src_${s.source}`, { defaultValue: s.source })}</td>
                  <td style={num}>{plain(s.count)}</td>
                  <td style={num}>{baht(s.withheld)}</td>
                </tr>))}
              </tbody>
            </table>
          )}
          {!!d.missing?.length && (
            <div style={amber}>
              {t('pa_headless_whs_missing', { n: d.missing.length })}
              <ul style={{ margin: '5px 0 0', paddingLeft: 17 }}>
                {d.missing.map((m: any) => <li key={m.id}>{dmy(m.payDate)} · {m.payerName} · {baht(m.withheld)}</li>)}
              </ul>
            </div>
          )}
          {d.note && <div style={amber}>{d.note}</div>}
        </>
      )}
      {!periodId && <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('pa_headless_whs_no_period')}</div>}
      {msg && <div style={{ fontSize: 12.5, color: '#166534' }}>{msg}</div>}
      <Err e={err} />
    </div>
  );
}

/**
 * Each employee's annual withholding certificate. Read-only: it is the payroll lines already in the
 * system, grouped by employee, with the due date the server computed — within one month of leaving,
 * otherwise 15 February of the following year. `basis` and the overdue flag are the server's, not
 * re-derived here, so the screen cannot disagree with them.
 */
function EmployeeCertificates({ clientId }: { clientId: string }) {
  const { t } = useTranslation();
  const [year, setYear] = useState(new Date().getUTCFullYear());
  const [rows, setRows] = useState<any[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const load = useCallback(async () => {
    setBusy(true); setErr('');
    try { const r: any = await api.axEmployeeCertificates(clientId, year); setRows(r.certificates || []); }
    catch (e: any) { setErr(e.message); setRows(null); } finally { setBusy(false); }
  }, [clientId, year]);
  useEffect(() => { load(); }, [load]);
  return (
    <div>
      <div style={rule}>{t('pa_headless_ec_rule')}</div>
      <div style={row}>
        <label style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{t('pa_headless_ec_year')}<br />
          <input type="number" value={year} onChange={e => setYear(Number(e.target.value) || year)} style={{ ...inp, width: 90 }} /></label>
        <button style={btn} disabled={busy} onClick={load}>
          <i className={busy ? 'ti ti-loader' : 'ti ti-refresh'} aria-hidden /> {t('pa_headless_ec_reload')}
        </button>
      </div>
      {rows && rows.length ? (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={th}>{t('pa_headless_ec_employee')}</th>
            <th style={{ ...th, textAlign: 'right' }}>{t('pa_headless_ec_months')}</th>
            <th style={{ ...th, textAlign: 'right' }}>{t('pa_headless_ec_wage')}</th>
            <th style={{ ...th, textAlign: 'right' }}>{t('pa_headless_ec_withheld')}</th>
            <th style={th}>{t('pa_headless_ec_due_by')}</th>
          </tr></thead>
          <tbody>{rows.map(r => (
            <tr key={`${r.empId}|${r.idNumber}|${r.name}`}>
              <td style={td}>{r.name}{r.idNumber ? <span style={{ color: 'var(--text-tertiary)' }}> · {r.idNumber}</span> : null}
                {r.terminatedOn ? <span style={{ display: 'block', fontSize: 11, color: '#92400E' }}>{t('pa_headless_ec_left_on', { date: dmy(r.terminatedOn) })}</span> : null}</td>
              <td style={num}>{plain(r.months)}</td>
              <td style={num}>{baht(r.wage)}</td>
              <td style={num}>{baht(r.withheld)}</td>
              <td style={{ ...td, color: r.overdue ? '#B42318' : 'var(--text-primary)', fontWeight: r.overdue ? 700 : 400 }}>
                {dmy(r.dueBy)}
                <span style={{ display: 'block', fontSize: 11, fontWeight: 400, color: 'var(--text-tertiary)' }}>{r.basis}</span>
              </td>
            </tr>))}
          </tbody>
        </table>
      ) : rows ? <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{t('pa_headless_ec_empty', { year })}</div> : null}
      <Err e={err} />
    </div>
  );
}

export default function Headless({ clientId, periodId, periodClosed }:
  { clientId: string; periodId: string; periodClosed: boolean }) {
  const { t } = useTranslation();
  const readOnly = useReadOnlySeat();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--text-tertiary)' }}>
        {t('pa_headless_module_title')}
      </div>
      <Section title={`${t('pa_headless_sec_wa_name')} · ${t('pa_headless_sec_wa')}`} subtitle={t('pa_headless_sec_wa_sub')}>
        <WorkingAccount clientId={clientId} periodId={periodId} />
      </Section>
      <Section title={`${t('pa_headless_sec_stock_name')} · ${t('pa_headless_sec_stock')}`} subtitle={t('pa_headless_sec_stock_sub')}>
        <StockAccount clientId={clientId} periodId={periodId} />
      </Section>
      <Section title={`${t('pa_headless_sec_aud_name')} · ${t('pa_headless_sec_aud')}`} subtitle={t('pa_headless_sec_aud_sub')}>
        <Auditor clientId={clientId} periodId={periodId} />
      </Section>
      <Section title={`${t('pa_headless_sec_pp30_name')} · ${t('pa_headless_sec_pp30')}`} subtitle={t('pa_headless_sec_pp30_sub')}>
        <Pp30Coverage clientId={clientId} />
      </Section>
      <Section title={`${t('pa_headless_sec_fw_name')} · ${t('pa_headless_sec_fw')}`} subtitle={t('pa_headless_sec_fw_sub')}>
        <Framework clientId={clientId} periodId={periodId} closed={periodClosed} readOnly={readOnly} />
      </Section>
      <Section title={`${t('pa_headless_sec_sh_name')} · ${t('pa_headless_sec_sh')}`} subtitle={t('pa_headless_sec_sh_sub')}>
        <Shareholders clientId={clientId} readOnly={readOnly} />
      </Section>
      <Section title={t('pa_headless_sec_pkg')} subtitle={t('pa_headless_sec_pkg_sub')}>
        <EfilingPackage clientId={clientId} periodId={periodId} readOnly={readOnly} />
      </Section>
      <Section title={t('pa_headless_sec_cit')} subtitle={t('pa_headless_sec_cit_sub')}>
        <TaxTreatment clientId={clientId} periodId={periodId} readOnly={readOnly} />
      </Section>
      <Section title={`${t('pa_headless_sec_fx_name')} · ${t('pa_headless_sec_fx')}`} subtitle={t('pa_headless_sec_fx_sub')}>
        <FxApproval clientId={clientId} readOnly={readOnly} />
      </Section>
      <Section title={t('pa_headless_sec_ret')} subtitle={t('pa_headless_sec_ret_sub')}>
        <Retention clientId={clientId} readOnly={readOnly} />
      </Section>
      <Section title={t('pa_headless_sec_agm')} subtitle={t('pa_headless_sec_agm_sub')}>
        <Agm clientId={clientId} />
      </Section>
      <Section title={t('pa_headless_sec_wht')} subtitle={t('pa_headless_sec_wht_sub')}>
        <WhtLiability clientId={clientId} />
      </Section>
      <Section title={t('pa_headless_sec_sso')} subtitle={t('pa_headless_sec_sso_sub')}>
        <EmployerSso clientId={clientId} readOnly={readOnly} />
      </Section>
      <Section title={t('pa_headless_sec_rev')} subtitle={t('pa_headless_sec_rev_sub')}>
        <AssetBases clientId={clientId} periodId={periodId} readOnly={readOnly} />
      </Section>
      <Section title={`${t('pa_headless_sec_arc_name')} · ${t('pa_headless_sec_arc')}`} subtitle={t('pa_headless_sec_arc_sub')}>
        <ArchiveNumbers clientId={clientId} />
      </Section>
      <Section title={t('pa_headless_sec_whs')} subtitle={t('pa_headless_sec_whs_sub')}>
        <WhtSuffered clientId={clientId} periodId={periodId} />
      </Section>
      <Section title={`${t('pa_headless_sec_ec_name')} · ${t('pa_headless_sec_ec')}`} subtitle={t('pa_headless_sec_ec_sub')}>
        <EmployeeCertificates clientId={clientId} />
      </Section>
    </div>
  );
}
